import { type AxiosError } from 'axios'
import _get from 'lodash/get'
import _isArray from 'lodash/isArray'
import _isEmpty from 'lodash/isEmpty'
import _isObject from 'lodash/isObject'
import _isString from 'lodash/isString'

const DEFAULT_ERROR_MESSAGE =
  'An error occurred. Please contact support if the problem persists.'

export function formatErrorMessage(message: Any, depth = 0) {
  const prefix = ''.padStart(depth * 2, ' ')

  if (!_isObject(message)) {
    return prefix + message
  }

  const keys = Object.keys(message)
  const singleKey = keys.length === 1

  if (singleKey) {
    return prefix + (message as Record<string, Any>)[keys[0]]
  }

  return Object.keys(message).reduce((fullMessage, messageKey) => {
    const msgObj = message as Record<string, Any>
    const messageValue = msgObj[messageKey]
    let nextMessage = messageValue
    if (_isArray(messageValue)) {
      if (messageValue.some(_isObject)) {
        nextMessage = messageValue
          .map((value: Any) => `\n${prefix}${formatErrorMessage(value, depth + 1)}`)
          .filter((value: string) => !!value.trim())
          .join(' , ')
      } else {
        nextMessage = messageValue.join(' , ')
      }
    } else if (_isObject(messageValue)) {
      nextMessage = `\n${prefix}${formatErrorMessage(messageValue, depth + 1)}`
    }

    const currentMessage =
      messageKey === '__all__' ? nextMessage : `${messageKey} - ${nextMessage}`

    return `${fullMessage}${prefix}${currentMessage}\n`
  }, '')
}

export function getErrorMessage(
  e: Error | AxiosError,
  {
    defaultMessage = DEFAULT_ERROR_MESSAGE,
    obscureServerErrors = true,
  }: {
    defaultMessage?: string
    obscureServerErrors?: boolean
  } = {}
) {
  if (!e) {
    return defaultMessage
  }

  const status = _get(e, 'response.status')
  const isServerError = status && status >= 500

  if (isServerError && obscureServerErrors) {
    return defaultMessage
  }

  const responseData = _get(e, 'response.data', {})

  const dataMessage =
    _get(responseData, 'message', '') || _get(responseData, 'detail', '') || ''

  if (dataMessage) {
    return formatErrorMessage(dataMessage)
  }

  if (!_isEmpty(responseData)) {
    if (_isString(responseData)) {
      return responseData
    }
    return formatErrorMessage(responseData)
  }

  return formatErrorMessage(
    _get(e, 'response.statusText', '') || _get(e, 'message', '') || defaultMessage
  )
}
